import type { SupabaseClient } from "@supabase/supabase-js";

export interface GuardSiteGeofenceStatus {
  routeAssignmentId: string;
  siteId: string;
  siteName: string | null;
  /** null = unknown / not yet evaluated with good GPS */
  inside: boolean | null;
  consecutiveOutsideCount: number;
  lastEvaluatedAt: string | null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function mapGuardSiteGeofenceStatus(raw: Record<string, unknown>): GuardSiteGeofenceStatus | null {
  const routeAssignmentId = asString(raw.route_assignment_id);
  const siteId = asString(raw.site_id);
  if (routeAssignmentId === null || siteId === null) {
    return null;
  }
  return {
    routeAssignmentId,
    siteId,
    siteName: asString(raw.site_name),
    inside: typeof raw.inside === "boolean" ? raw.inside : null,
    consecutiveOutsideCount: asNumber(raw.consecutive_outside_count) ?? 0,
    lastEvaluatedAt: asString(raw.last_evaluated_at),
  };
}

export function indexGeofenceStatusByAssignment(
  rows: readonly GuardSiteGeofenceStatus[],
): Record<string, GuardSiteGeofenceStatus> {
  const index: Record<string, GuardSiteGeofenceStatus> = {};
  for (const row of rows) {
    index[row.routeAssignmentId] = row;
  }
  return index;
}

/** Management-scoped guard × site boundary state for on-shift assignments. */
export async function fetchGuardSiteGeofenceStatus(
  client: SupabaseClient,
  accountId: string,
): Promise<GuardSiteGeofenceStatus[]> {
  const { data, error } = await client
    .from("management_guard_site_geofence_status")
    .select("route_assignment_id, site_id, site_name, inside, consecutive_outside_count, last_evaluated_at")
    .eq("account_id", accountId);
  if (error !== null) {
    throw new Error("Could not load guard site geofence status.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data
    .map((row) => mapGuardSiteGeofenceStatus(row as Record<string, unknown>))
    .filter((row): row is GuardSiteGeofenceStatus => row !== null);
}

export function isOutsideSiteBoundary(status: GuardSiteGeofenceStatus | undefined): boolean {
  return status?.inside === false;
}
